/**
 * MarketingFooter — bottom-of-page footer for every Colony marketing site.
 *
 * Renders:
 *   - Brand block (wordmark + tagline) sourced from @puffin-core/brand-assets.
 *   - 1–N footer columns of links (configurable per app via `columns` prop).
 *   - Optional social links row.
 *   - Legal row: copyright + privacy/terms links.
 *
 * Consumes `.site-footer`, `.footer-main`, `.footer-column-list`,
 * `.footer-column-title`, `.footer-legal` from @puffin-core/shared-styles.
 *
 * @module @puffin-core/marketing-shell/MarketingFooter
 */

import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getBrand, resolveBrandTagline } from '@puffin-core/brand-assets';
import { copyrightYear as currentCopyrightYear } from '@puffin-core/temporal-core/copyright-year';
import { SmartLink } from '../links/SmartLink.jsx';
import { SocialIcon } from '../social/SocialIcon.jsx';
import { appKeyPropType } from './appKeyPropType.js';
import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';
import { useOptionalCookieConsent } from './CookieConsent.jsx';
import { CookieSettingsButton, canReopenConsent } from './CookieSettingsButton.jsx';

/**
 * FooterBrandColumn — the wordmark + tagline block that leads the footer.
 * Module-scope so the footer's own JSX never nests h2 > span.
 */
function FooterBrandColumn({ wordmarkPrefix, wordmarkSuffix, tagline }) {
  return (
    <div className="footer-column-list">
      <h2 className="footer-column-title brand-name">
        <span className="brand-name-prefix">{wordmarkPrefix}</span>
        <span className="brand-name-suffix">{wordmarkSuffix}</span>
      </h2>
      {tagline ? <p>{tagline}</p> : null}
    </div>
  );
}

FooterBrandColumn.propTypes = {
  wordmarkPrefix: PropTypes.string.isRequired,
  wordmarkSuffix: PropTypes.string.isRequired,
  tagline: PropTypes.string,
};

/**
 * FooterLinkColumn — one heading + its list of links. Owns the per-link map so
 * the footer itself never nests one map inside another.
 */
function FooterLinkColumn({ column, newTabLabel }) {
  return (
    <div className="footer-column-list">
      <h3 className="footer-column-title">{column.heading}</h3>
      <ul>
        {column.links.map((link) => (
          <li key={link.key}>
            {/* SmartLink decides the element; an external link opens a new tab, announced to screen readers. */}
            <SmartLink href={link.to} newTab={Boolean(link.external)} newTabLabel={newTabLabel}>
              {link.label}
            </SmartLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

FooterLinkColumn.propTypes = {
  newTabLabel: PropTypes.string.isRequired,
  column: PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    heading: PropTypes.string.isRequired,
    links: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string.isRequired,
        to: PropTypes.string.isRequired,
        external: PropTypes.bool,
      }),
    ).isRequired,
  }).isRequired,
};

/**
 * FooterLegal — the copyright line, the legal pages that exist, an optional site-wide disclosure, and
 * an optional platform credit. A legal link with no destination is left out (LIFE-SHORTLISTED-001
 * P10.4), so a tenant site that declared no legal pages shows none rather than a link that 404s.
 *
 * The credit (LINKABLE-ASSETS-001 E1, owner ruling R-65) is a `<p class="footer-attribution">`: the
 * translated prefix as text and the platform's brand NAME as the one anchor — a `<SmartLink>` to the
 * platform's marketing origin, FOLLOWED (no `sponsored`, no `nofollow`; `rel="noopener noreferrer"`
 * is what SmartLink gives every external link). Absent or null → nothing in its place.
 */
function FooterLegal({ notice, links, disclosure, attribution }) {
  // "Cookie settings" joins the legal links wherever the visitor has a consent choice to change
  // (LIFE-SHORTLISTED-001, owner ruling G6). The ONE footer carries it, so no site wires it. With no
  // consent surface (the server-rendered blog) or nothing to reopen, the row is as it was.
  const offersCookieSettings = canReopenConsent(useOptionalCookieConsent());
  return (
    <>
      <div className="footer-legal">
        <span>{notice}</span>
        {links.length > 0 || offersCookieSettings ? (
          <span className="footer-legal-links">
            {links.map((link, index) => (
              <Fragment key={link.key}>
                {index > 0 ? ' · ' : null}
                <SmartLink href={link.to}>{link.label}</SmartLink>
              </Fragment>
            ))}
            {offersCookieSettings && links.length > 0 ? ' · ' : null}
            <CookieSettingsButton className="footer-legal-action" />
          </span>
        ) : null}
      </div>
      {disclosure ? <p className="footer-disclosure">{disclosure}</p> : null}
      {attribution ? (
        <p className="footer-attribution">
          {attribution.prefix} <SmartLink href={attribution.href}>{attribution.label}</SmartLink>
        </p>
      ) : null}
    </>
  );
}

const attributionPropType = PropTypes.shape({
  prefix: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  rel: PropTypes.string,
});

FooterLegal.propTypes = {
  notice: PropTypes.string.isRequired,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      to: PropTypes.string.isRequired,
      label: PropTypes.string,
    }),
  ).isRequired,
  disclosure: PropTypes.string,
  attribution: attributionPropType,
};

/**
 * @typedef {Object} FooterLink
 * @property {string|number} key
 * @property {string} label
 * @property {string} to        Internal route. Use `href` for external links.
 * @property {boolean} [external] When true, the link opens in a new tab, announced to screen readers (SmartLink).
 */

/**
 * @typedef {Object} FooterColumn
 * @property {string|number} key
 * @property {string} heading
 * @property {readonly FooterLink[]} links
 */

/**
 * @typedef {Object} SocialLink
 * @property {string|number} key
 * @property {string} label    Visible label (shown for a11y / no-image fallback).
 * @property {string} href     Outbound URL.
 */

/**
 * @param {Object} props
 * @param {string} props.appKey                  brand-assets key.
 * @param {string} [props.tagline]               Override the brand tagline.
 * @param {readonly FooterColumn[]} [props.columns]
 * @param {readonly React.ReactNode[]} [props.extraColumns]  PROTECTOR-MKT-002:
 *      Pre-rendered column nodes slotted BETWEEN the standard columns and
 *      the social-links column. Used to host primitives whose internal
 *      shape does not fit the rigid heading/links/link-shape of `columns`
 *      (e.g. <CredentialsFooterColumn> with multi-line regulatory text).
 * @param {readonly SocialLink[]} [props.socialLinks]
 * @param {string} [props.privacyLabel] - defaults to the `footer.privacyPolicy` catalog word
 * @param {string} [props.privacyTo='/privacy']
 * @param {string} [props.termsLabel] - defaults to the `footer.termsOfService` catalog word
 * @param {string} [props.termsTo='/terms']
 * @param {string} [props.copyrightOwner] - who the copyright line names; the brand's `appName` by
 *   default. A white-label tenant site passes its LEGAL ENTITY ("Holdings Nest Inc. d/b/a Life
 *   Shortlisted", LIFE-SHORTLISTED-001 P10.4).
 * @param {string} [props.disclosure] - a site-wide statement shown under the copyright line (an
 *   affiliate notice); absent → nothing is rendered in its place.
 * @param {?string} [props.disclosurePageTo] - the site's disclosure page, linked beside Privacy and
 *   Terms when given.
 * @param {string} [props.disclosurePageLabel]
 * @param {?string} [props.accessibilityTo] - the site's accessibility statement, linked beside the
 *   other legal pages when given (DTOWNRETAIL-SITE-001 U7).
 * @param {string} [props.accessibilityLabel]
 * @param {?{prefix: string, label: string, href: string, rel?: string}} [props.attribution] - the
 *   tenant site's platform credit under the legal row (LINKABLE-ASSETS-001 E1): `prefix` as text, the
 *   brand `label` as the one anchor to `href`. Built by `buildTenantFooter`; null or absent → nothing.
 * @param {string} [props.ariaLabel] - the footer landmark's name; `<appName> site footer` by default
 *
 * `privacyTo` / `termsTo` accept `null` to OMIT that link: a tenant site links only the legal pages it
 * declared, and one that declared none shows none (a `/privacy` default would 404 there).
 * @param {string|null} [props.phone]  PROTECTOR-VOIP-001: E.164 contact
 *      number. Defaults to a self-describing `brandIdentity.phone` (no registry brand carries
 *      one since LIFE-SHORTLISTED-001 Q-LS-32). When non-null, the
 *      footer renders a "Contact" column with a `tel:` link; absent, no column.
 * @param {string} [props.phoneDisplay]  Human-readable display text for the
 *      phone (e.g. vanity "(541) 527-SAFE", or @puffin-core/phone `formatPhoneNumber(phone)`).
 *      Defaults to `phone` as delivered: the footer does not format.
 * @param {string} [props.phoneColumnHeading] - defaults to the `footer.contact` catalog word
 * @param {{appName:string,wordmarkPrefix:string,wordmarkSuffix:string,tagline?:string,phone?:string}} [props.brandIdentity]
 *   BLOG-VANITY-SSR: self-describing white-label brand chrome. When provided, the
 *   wordmark + appName + (optional) tagline/phone come from here instead of
 *   `getBrand(appKey)` (used by vanity blog appKeys absent from brand-assets).
 */
export default function MarketingFooter({
  appKey,
  tagline,
  columns = [],
  extraColumns,
  socialLinks = [],
  privacyLabel: hostPrivacyLabel,
  privacyTo = '/privacy',
  termsLabel: hostTermsLabel,
  termsTo = '/terms',
  copyrightOwner,
  disclosure,
  disclosurePageTo = null,
  accessibilityTo = null,
  accessibilityLabel,
  privacyRequestTo = null,
  privacyRequestLabel,
  disclosurePageLabel,
  attribution,
  ariaLabel,
  phone,
  phoneDisplay,
  phoneColumnHeading: hostPhoneColumnHeading,
  copyrightYear,
  brandIdentity,
}) {
  // AAS-I18N-002 Q-F4 — the brand-assets fallback tagline is COPY, resolved
  // at render for the active language. Most sites pass their own `tagline`
  // (a `t()` result), so this arm is the degrade path — and a degrade path
  // that is hard-English is how the defect hid the first time.
  const { i18n } = useTranslation();
  const renderableExtraColumns = Array.isArray(extraColumns)
    ? extraColumns.filter((node) => node !== null && node !== undefined)
    : [];
  // BLOG-VANITY-SSR: a self-describing `brandIdentity` (white-label vanity blog)
  // overrides the brand-assets lookup so the footer never calls `getBrand` for an
  // appKey the registry doesn't carry (customcarvers). Absent → `getBrand(appKey)`
  // (the 9 ParcelPuffin-family apps, unchanged). `getBrand` is only called on fallback.
  const brand = brandIdentity || getBrand(appKey);
  // A self-describing `brandIdentity` (BLOG-VANITY-SSR white-label chrome)
  // carries its own single-language `tagline` string and is NOT a registry
  // entry, so it never reaches the per-locale resolver.
  const brandTagline = brandIdentity
    ? brandIdentity.tagline
    : resolveBrandTagline(brand, i18n?.language);
  // BLOG-STD-001: the copyright year is normally derived from the wall clock,
  // but a publish-time-prerendered page is rendered on the server at sync time
  // and hydrated in the browser later — possibly across a year boundary. The
  // prerender harness seeds `copyrightYear` so the server and client render the
  // identical string (no hydration mismatch). Live CSR pages omit it and fall
  // back to the current year.
  const year =
    typeof copyrightYear === 'number' && Number.isFinite(copyrightYear)
      ? copyrightYear
      : currentCopyrightYear();
  // The copyright line is marketing-shell's own sentence (`marketingShell` namespace), translated
  // whole: it used to be assembled here with an English ". All rights reserved." on every Spanish
  // page (LIFE-SHORTLISTED-001 F-Q7-1).
  const t = useMarketingShellTranslate();
  // The legal links' and the contact column's words: the caller's, else the catalog's in the page's
  // language — never English default parameters, which every Spanish page would render.
  const privacyLabel = hostPrivacyLabel ?? t('footer.privacyPolicy');
  const termsLabel = hostTermsLabel ?? t('footer.termsOfService');
  const phoneColumnHeading = hostPhoneColumnHeading ?? t('footer.contact');

  // Fall back to a self-describing brand identity's phone when the caller passes none (registry
  // brands carry no phone). `phone === null`, or no phone on the brand → no contact column.
  const footerPhone = phone === undefined ? brand.phone : phone;
  // The footer never formats a number: a caller passes `phoneDisplay` from the ONE formatter
  // (@puffin-core/phone formatPhoneNumber), else the number shows as delivered (L-R7CLOSE row 53). Its
  // own `+1` regex formatter was a second rule, and the ONE formatter's plan metadata does not belong in
  // every marketing page's eager chunk (PERF-FECHUNK-001).
  const footerPhoneDisplay = footerPhone ? phoneDisplay || String(footerPhone) : null;

  // The three optional trailing columns, resolved ahead of the return so the
  // footer's returned tree is just its column sequence.
  const phoneColumn = footerPhone ? (
    <div className="footer-column-list">
      <h3 className="footer-column-title">{phoneColumnHeading}</h3>
      <ul>
        <li>
          <a href={`tel:${footerPhone}`}>{footerPhoneDisplay}</a>
        </li>
      </ul>
    </div>
  ) : null;

  const extraColumnsBlock =
    renderableExtraColumns.length > 0 ? (
      <div className="footer-main-extra-columns">{renderableExtraColumns}</div>
    ) : null;

  const socialColumn =
    socialLinks.length > 0 ? (
      <div className="footer-column-list">
        <h3 className="footer-column-title">{t('footer.connect')}</h3>
        <ul>
          {/* LIFE-SHORTLISTED-001 U7: SmartLink, like every other footer column — these were the one raw
              `<a target="_blank">`, a new tab with no notice to a screen reader. */}
          {socialLinks.map((link) => (
            <li key={link.key}>
              <SmartLink
                href={link.href}
                newTab
                newTabLabel={t('footer.opensInNewTab')}
                className="footer-social-link"
              >
                <SocialIcon platform={link.key} />
                <span>{link.label}</span>
              </SmartLink>
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  return (
    <footer
      className="site-footer"
      aria-label={ariaLabel || t('footer.siteFooter', { appName: brand.appName })}
    >
      <div className="footer-main">
        <FooterBrandColumn
          wordmarkPrefix={brand.wordmarkPrefix}
          wordmarkSuffix={brand.wordmarkSuffix}
          tagline={tagline || brandTagline}
        />

        {columns.map((column) => (
          <FooterLinkColumn
            key={column.key}
            column={column}
            newTabLabel={t('footer.opensInNewTab')}
          />
        ))}

        {phoneColumn}

        {extraColumnsBlock}

        {socialColumn}
      </div>

      <FooterLegal
        notice={t('footer.copyright', { year, owner: copyrightOwner || brand.appName })}
        links={[
          { key: 'privacy', to: privacyTo, label: privacyLabel },
          { key: 'terms', to: termsTo, label: termsLabel },
          { key: 'disclosure', to: disclosurePageTo, label: disclosurePageLabel },
          { key: 'accessibility', to: accessibilityTo, label: accessibilityLabel },
          { key: 'privacyRequest', to: privacyRequestTo, label: privacyRequestLabel },
        ].filter((link) => link.to)}
        disclosure={disclosure}
        attribution={attribution}
      />
    </footer>
  );
}

MarketingFooter.propTypes = {
  appKey: appKeyPropType,
  tagline: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      heading: PropTypes.string.isRequired,
      links: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          label: PropTypes.string.isRequired,
          to: PropTypes.string.isRequired,
          external: PropTypes.bool,
        }),
      ).isRequired,
    }),
  ),
  extraColumns: PropTypes.arrayOf(PropTypes.node),
  socialLinks: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    }),
  ),
  privacyLabel: PropTypes.string,
  privacyTo: PropTypes.string,
  disclosurePageTo: PropTypes.string,
  disclosurePageLabel: PropTypes.string,
  accessibilityTo: PropTypes.string,
  accessibilityLabel: PropTypes.string,
  privacyRequestTo: PropTypes.string,
  privacyRequestLabel: PropTypes.string,
  copyrightOwner: PropTypes.string,
  disclosure: PropTypes.string,
  attribution: attributionPropType,
  ariaLabel: PropTypes.string,
  termsLabel: PropTypes.string,
  termsTo: PropTypes.string,
  phone: PropTypes.string,
  phoneDisplay: PropTypes.string,
  phoneColumnHeading: PropTypes.string,
  copyrightYear: PropTypes.number,
  // BLOG-VANITY-SSR: self-describing white-label brand chrome (overrides getBrand).
  brandIdentity: PropTypes.shape({
    appName: PropTypes.string.isRequired,
    wordmarkPrefix: PropTypes.string.isRequired,
    wordmarkSuffix: PropTypes.string.isRequired,
    tagline: PropTypes.string,
    phone: PropTypes.string,
  }),
};
